import { useRef } from 'react';

import { useMutation, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router';

import { DASHBOARD_DEFAULT_BASE_NAME } from '@/entities/dashboard';
import type { UserConfig } from '@/entities/user-config';
import { userConfigQueryKey } from '@/features/_shared/user';
import { TAB_DEFAULT_BASE_NAME } from '@/features/dashboard/constants/names';
import { getUniqueDashboardName } from '@/features/dashboard/lib/getUniqueDashboardName';
import {
  selectCurrentTabName,
  selectDashboardName,
  selectEditStateForSave,
  selectInitialDashboardName
} from '@/features/dashboard/state/dashboardEdit.selectors';
import { mapDraftToServerPayload } from '@/features/dashboard/state/lib/mapping';
import type { DashboardTab } from '@/features/dashboard/types';
import { usePreferences } from '@/features/profile';
import useSnackbarNotifications from '@/shared/hooks/useSnackbarNotifications';
import { store } from '@/store';

import { dashboardOptions } from '../dashboard.options';
import {
  invalidateAndBroadcast,
  saveDashboardsBulkToApi,
  saveDashboardToApi
} from './dashboardMutation.api';
import type { DashboardUpdateInput } from './dashboardMutation.types';
import {
  buildDashboardPath,
  buildMergedPayload,
  buildOptimisticSaveCache,
  createHomepageOption,
  homepageMatchesDashboard,
  isRenameOperation
} from './dashboardMutation.utils';

export const SAVE_SNACKBAR_KEY = 'dashboard-save';

type SaveDashboardResult = {
  dashboardName: string;
  initialDashboardName: string;
  tabName: string;
  fullPayload: Record<string, DashboardTab>;
};

const getCurrentHomepageValue = (
  queryClient: QueryClient,
  fallbackHomepageValue: string
): string => {
  const cachedHomepage = queryClient.getQueryData<UserConfig>(userConfigQueryKey)?.homepage;
  return cachedHomepage?.value.length ? cachedHomepage.value : fallbackHomepageValue;
};

const buildSaveDashboardResult = (baseCache: Record<string, DashboardTab>): SaveDashboardResult => {
  const state = store.getState();
  const editState = selectEditStateForSave(state);
  const dashboardName = selectDashboardName(state);
  const initialDashboardName = selectInitialDashboardName(state);
  const tabName = selectCurrentTabName(state);

  const payload = mapDraftToServerPayload(editState);
  const fullPayload = { ...baseCache, ...payload };

  if (dashboardName !== initialDashboardName && initialDashboardName in fullPayload) {
    delete fullPayload[initialDashboardName];
  }

  return { dashboardName, initialDashboardName, tabName, fullPayload };
};

export const useUpdateDashboard = (
  dashboardName = DASHBOARD_DEFAULT_BASE_NAME,
  successMessage = 'Dashboard updated!',
  tabName = TAB_DEFAULT_BASE_NAME,
  oldDashboardName?: string
) => {
  const { showErrorSnackbar, showSuccessSnackbar } = useSnackbarNotifications();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { changeUserSetting, homepage } = usePreferences();
  const optimisticDashboardNameRef = useRef<string | null>(null);

  const queryKey = dashboardOptions.all();

  return useMutation({
    mutationKey: dashboardOptions.mutations.update(dashboardName, oldDashboardName),

    mutationFn: async (data: DashboardUpdateInput): Promise<string> => {
      const cached = queryClient.getQueryData<Record<string, DashboardTab>>(queryKey);
      const existingNames = cached ? Object.keys(cached) : [];

      const isNewDashboard = dashboardName === 'new';
      const effectiveDashboardName = isNewDashboard
        ? (optimisticDashboardNameRef.current ?? getUniqueDashboardName(existingNames))
        : dashboardName;

      let processedData = data;
      if (isNewDashboard && !Array.isArray(data) && 'new' in data) {
        const { new: newDashboardTabs, ...rest } = data;
        processedData = { ...rest, [effectiveDashboardName]: newDashboardTabs };
      }

      const payload = buildMergedPayload({
        data: processedData,
        dashboardName: effectiveDashboardName,
        tabName,
        oldDashboardName,
        cached
      });

      await saveDashboardToApi(effectiveDashboardName, payload, oldDashboardName);

      return effectiveDashboardName;
    },

    onMutate: async (data: DashboardUpdateInput) => {
      await queryClient.cancelQueries({ queryKey: dashboardOptions.all() });
      const previousCache = queryClient.getQueryData<Record<string, DashboardTab>>(queryKey);

      const isNewDashboard = dashboardName === 'new';
      const isRename = isRenameOperation(oldDashboardName, dashboardName);
      const optimisticDashboardName = isNewDashboard
        ? getUniqueDashboardName(Object.keys(previousCache ?? {}))
        : dashboardName;

      let optimisticData = data;
      if (isNewDashboard && !Array.isArray(data) && 'new' in data) {
        const { new: newDashboardTabs, ...rest } = data;
        optimisticData = { ...rest, [optimisticDashboardName]: newDashboardTabs };
      }

      const optimisticTab = buildMergedPayload({
        data: optimisticData,
        dashboardName: optimisticDashboardName,
        tabName,
        oldDashboardName,
        cached: previousCache
      });

      if (isNewDashboard) {
        optimisticDashboardNameRef.current = optimisticDashboardName;
        queryClient.setQueryData(queryKey, {
          ...previousCache,
          [optimisticDashboardName]: optimisticTab
        });
        navigate(buildDashboardPath(optimisticDashboardName, tabName));
      } else if (isRename && oldDashboardName) {
        const optimisticPayload = buildOptimisticSaveCache(
          previousCache ?? {},
          { [dashboardName]: optimisticTab },
          dashboardName,
          oldDashboardName
        );
        queryClient.setQueryData(queryKey, optimisticPayload);
        navigate(buildDashboardPath(dashboardName, tabName));
      } else {
        queryClient.setQueryData(queryKey, { ...previousCache, [dashboardName]: optimisticTab });
      }

      return { previousCache, oldDashboardName, dashboardName, optimisticDashboardName, tabName };
    },

    onError: (_error, _data, context) => {
      if (context?.previousCache) {
        queryClient.setQueryData(queryKey, context.previousCache);
      }
      if (context?.dashboardName === 'new') {
        navigate('/dashboard/new');
      } else if (
        context?.oldDashboardName &&
        isRenameOperation(context.oldDashboardName, context.dashboardName)
      ) {
        navigate(buildDashboardPath(context.oldDashboardName, context.tabName));
      }
      optimisticDashboardNameRef.current = null;
      showErrorSnackbar(
        "Oops! We couldn't save your dashboard. Please try again.",
        undefined,
        SAVE_SNACKBAR_KEY
      );
    },

    onSuccess: async effectiveDashboardName => {
      if (successMessage) {
        showSuccessSnackbar(successMessage, SAVE_SNACKBAR_KEY);
      }

      const didRename = isRenameOperation(oldDashboardName, effectiveDashboardName);
      const homepageValue = getCurrentHomepageValue(queryClient, homepage.value);
      const shouldUpdateHomepage =
        (didRename &&
          oldDashboardName &&
          homepageMatchesDashboard(homepageValue, oldDashboardName)) ||
        (effectiveDashboardName === DASHBOARD_DEFAULT_BASE_NAME &&
          homepageValue === 'my-dashboard');

      if (shouldUpdateHomepage) {
        changeUserSetting('homepage', createHomepageOption(effectiveDashboardName), false);
      }

      await invalidateAndBroadcast(queryClient);
    },

    onSettled: () => {
      optimisticDashboardNameRef.current = null;
    }
  });
};

export const useSaveDashboard = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { showSuccessSnackbar } = useSnackbarNotifications();

  const { changeUserSetting, homepage } = usePreferences();
  const saveResultRef = useRef<SaveDashboardResult | null>(null);

  return useMutation({
    mutationKey: dashboardOptions.mutations.save(),

    mutationFn: async (): Promise<SaveDashboardResult> => {
      const saveResult =
        saveResultRef.current ??
        buildSaveDashboardResult(
          queryClient.getQueryData<Record<string, DashboardTab>>(dashboardOptions.all()) ?? {}
        );

      await saveDashboardsBulkToApi(saveResult.fullPayload);

      return saveResult;
    },

    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: dashboardOptions.all() });
      const previousCache = queryClient.getQueryData<Record<string, DashboardTab>>(
        dashboardOptions.all()
      );

      const saveResult = buildSaveDashboardResult(previousCache ?? {});
      saveResultRef.current = saveResult;
      queryClient.setQueryData(dashboardOptions.all(), saveResult.fullPayload);

      // Navigate immediately on rename so the URL matches the updated cache.
      // Without this, the old URL resolves against a cache that no longer has
      // the old dashboard name, causing a not-found render until onSuccess fires.
      if (isRenameOperation(saveResult.initialDashboardName, saveResult.dashboardName)) {
        navigate(buildDashboardPath(saveResult.dashboardName, saveResult.tabName));
      }

      return { previousCache, ...saveResult };
    },

    onError: (_error, _variables, context) => {
      if (context?.previousCache) {
        queryClient.setQueryData(dashboardOptions.all(), context.previousCache);
      }
      // Undo optimistic navigation so the URL stays consistent with the restored cache.
      if (context && isRenameOperation(context.initialDashboardName, context.dashboardName)) {
        navigate(buildDashboardPath(context.initialDashboardName, context.tabName));
      }
    },

    onSuccess: async ({ dashboardName, initialDashboardName }) => {
      showSuccessSnackbar('Dashboard saved!', SAVE_SNACKBAR_KEY);
      const homepageValue = getCurrentHomepageValue(queryClient, homepage.value);

      if (
        isRenameOperation(initialDashboardName, dashboardName) &&
        homepageMatchesDashboard(homepageValue, initialDashboardName)
      ) {
        changeUserSetting('homepage', createHomepageOption(dashboardName), false);
      }

      await invalidateAndBroadcast(queryClient);
    },

    onSettled: () => {
      saveResultRef.current = null;
    }
  });
};
